import type { AreaKind } from './areaCatalog';
import { agreementSigned } from './agreement';
import type { AiObservationContent, AiObservationKind } from './aiObservation';
import {
  BUILDING_THREAT_PATTERNS,
  CONCEALMENT_PATTERNS,
  HAZARD_PATTERNS,
  NY_SCOPE_SUBJECTS,
  PRESENCE_QUESTION_PATTERNS,
  scopeSubject,
  subjectOfTemplateRow,
  type LegalStatus,
  type ScopeDisposition,
  type ScopeSubject,
  type ScopeSubjectId
} from './authority/ny/scopeSubjects';
import type { LimitationTemplateId } from './authority/ny/limitationTemplates';
import { NY_AUTHORITY_VERSION, NY_CHECKLIST_AUTHORITY_VERSION } from './authority/ny/version';
import { prohibitedPhrase, type ProhibitedPhraseId } from './authority/ny/prohibitedPhrases';
import type { HeatingFuel, UtilitySource } from './descriptors';
import { assertedPropertyState, canCarryLimitedPortions, type BasementFinish, type BasementType, type PropertyType, type UnableReason, type V2ChecklistItem, type V2Inspection } from './inspection';
import { contextKindOf, legalReviewBlockOf, type InspectionContextKind } from './inspectionContext';
import { jurisdictionStatusOf } from './jurisdiction';
import { stripProhibitedSentences, type ProhibitedHit } from './wordingGuard';

// THE DETERMINISTIC NEW YORK SCOPE GATE (NY F1 Round 4; frozen plan §8 / §9; AI policy §3;
// runtime contract §4), together with the two things it reads: the per-inspection scope SNAPSHOT
// (plan §5.4) and the PACKET derived from it (AI policy §7).
//
// What this module is for: the AI is the lowest authority in the product (master §1). It may
// propose; New York decides what may become inspector work; the inspector decides everything else.
// A rule that lives only in a prompt is not enforced (PP-12), so every AI result passes through a
// pure, versioned, offline function here before it can reach a card.
//
// Properties this module keeps, each pinned by tests/domain/scopeGate.test.ts (plan §9.2):
//   i    no `suggestedItem` about a subject that is not in scope ever reaches the inspector as a task;
//   ii   no gate output writes an ItemStatus — the gate returns decisions, nothing else;
//   iii  an obstruction never creates a task on itself: it converts to a limitation on the concealed
//        in-scope component or portion (PP-22 — never a whole-row Unable);
//   iv   every decision carries the authority version, the legal status and the deciding rule ids;
//   v    determinism — the same (result, packet, context) yields the same decision, with no model
//        call, no clock and no randomness;
//   vi   AI never raises scope: no decision can turn a not-required or excluded subject into
//        MANDATORY_IN_SCOPE, and no Axis-C value (the AI result type, an ItemStatus) moves Axis A or B;
//   vii  a LEGAL_REVIEW_BLOCK inspection has no packet at all, so no AI call is made for it;
//   viii the fixed agreement clauses never survive in AI output (P-14, wording guard).
//
// Owner ruling OD-13 (2026-09-09) amends the frozen plan: a `REJECTED_BY_SCOPE` result is no longer
// dropped silently. It becomes a SCOPE REMINDER — never a defect, never a task, never in the report
// draft, no confidence shown — with two inspector actions: record a voluntary observation under
// Other Findings, or acknowledge it. The run log keeps every decision either way.
//
// Pure: no store, no i18n, no network, no clock.

// ---------- the snapshot (plan §5.4; runtime contract §2 / §6) ----------

export interface ScopeDispositionRecord {
  legalStatus: LegalStatus;
  disposition: ScopeDisposition;
  ruleIds: string[];
}

// The property facts that decide a disposition. Copied into the snapshot so a later edit to the
// record cannot silently rewrite what was frozen at signing.
export interface SnapshotPropertyFacts {
  propertyType: PropertyType | null;
  yearBuilt: number | null;
  basementType: BasementType | null;
  basementFinish: BasementFinish | null;
  previouslyOccupied: boolean | null;
  waterSupplySource: UtilitySource | null;
  wasteDisposalSource: UtilitySource | null;
  heatingFuel: HeatingFuel | null;
}

export interface InspectionScopeSnapshot {
  jurisdiction: 'NY';
  authorityVersion: string;
  agreementAuthorityVersion: string;
  checklistAuthorityVersion: string;
  // = the agreement's signedAt. The snapshot is frozen at signing and never back-filled (plan §6).
  takenAt: number;
  propertyFacts: SnapshotPropertyFacts;
  dispositions: Record<string, ScopeDispositionRecord>;
  contractAdded: string[];
  clientExclusions: string[];
  contextKind: InspectionContextKind;
  legalReviewBlock: { blockerId: string; lrIds: string[] } | null;
}

// What the signed agreement adds to, or removes from, the New York minimum.
//
// R5 SEAM (OD-11 agreement v2): the version key below is the whole registry. When Round 5 raises
// `AGREEMENT_DOCUMENT_VERSION`, it registers the new version here with whatever ADDITIONAL SERVICES
// and client-requested exclusions that document carries. An unknown version resolves to "nothing
// added, nothing excluded": the New York minimum still applies, scope is never widened by default,
// and a client exclusion is never assumed — 197-5.16(q) admits one only on the client's request, and
// a form-imposed exclusion is not a client request (F-10).
export interface AgreementScope {
  contractAdded: readonly ScopeSubjectId[];
  clientExclusions: readonly ScopeSubjectId[];
}

const EMPTY_AGREEMENT_SCOPE: AgreementScope = Object.freeze({ contractAdded: Object.freeze([]), clientExclusions: Object.freeze([]) });

// The approved production copy of 2026-08-25 states "ADDITIONAL SERVICES AGREED FOR THIS PROPERTY:
// None." and carries no client-requested exclusion.
const AGREEMENT_SCOPE_BY_VERSION: Readonly<Record<string, AgreementScope>> = Object.freeze({});

export function agreementScopeFor(documentVersion: string): AgreementScope {
  return AGREEMENT_SCOPE_BY_VERSION[documentVersion] ?? EMPTY_AGREEMENT_SCOPE;
}

export function snapshotPropertyFactsOf(inspection: Pick<V2Inspection, 'property'>): SnapshotPropertyFacts {
  const p = inspection.property;
  return {
    propertyType: p.propertyType,
    yearBuilt: p.yearBuilt,
    basementType: p.basementType,
    basementFinish: p.basementFinish,
    previouslyOccupied: p.previouslyOccupied,
    waterSupplySource: p.waterSupplySource,
    wasteDisposalSource: p.wasteDisposalSource,
    heatingFuel: p.heatingFuel
  };
}

function propertyFactValue(facts: SnapshotPropertyFacts, fact: 'basementType' | 'heatingFuel' | 'propertyType'): string | null {
  switch (fact) {
    case 'basementType':
      return facts.basementType;
    case 'heatingFuel':
      return facts.heatingFuel;
    case 'propertyType':
      return facts.propertyType;
  }
}

// AXIS B — derived per inspection from Axis A + the property facts + the signed agreement, then
// frozen. Nothing here can raise a subject New York does not require into a mandatory one.
export function deriveDispositions(facts: SnapshotPropertyFacts, agreement: AgreementScope): Record<string, ScopeDispositionRecord> {
  const out: Record<string, ScopeDispositionRecord> = {};
  for (const s of NY_SCOPE_SUBJECTS) {
    out[s.id] = { legalStatus: s.legalStatus, disposition: dispositionOf(s, facts, agreement), ruleIds: [...s.ruleIds] };
  }
  return out;
}

function dispositionOf(s: ScopeSubject, facts: SnapshotPropertyFacts, agreement: AgreementScope): ScopeDisposition {
  if (agreement.clientExclusions.includes(s.id)) return 'CLIENT_EXCLUDED_IF_LAWFULLY_PERMITTED';
  switch (s.legalStatus) {
    case 'MANDATORY_BY_NY_SOP':
      return 'MANDATORY_IN_SCOPE';
    case 'CONDITIONAL_MANDATORY':
      return s.triggers.some((t) => t.kind === 'propertyFact' && propertyFactValue(facts, t.fact) !== null && t.oneOf.includes(propertyFactValue(facts, t.fact) as string))
        ? 'MANDATORY_IN_SCOPE'
        : 'CONDITIONAL_SCOPE';
    case 'NOT_REQUIRED_BY_NY_SOP':
      return agreement.contractAdded.includes(s.id) ? 'CONTRACT_INCLUDED' : 'OUT_OF_SCOPE_PRODUCT_POLICY';
    case 'STATUTORY_EXCLUSION':
      return 'STATUTORY_EXCLUSION';
    case 'EXPLICIT_PROHIBITION':
      return 'EXPLICIT_PROHIBITION';
    case 'LEGAL_REVIEW_REQUIRED':
      return 'LEGAL_REVIEW_REQUIRED';
  }
}

// The snapshot as it is taken AT AGREEMENT SIGNING. `null` when no New York authority package
// applies to this property (the state was never asserted, or it is not New York): the product must
// never fall back to New York rules for another state (PP-06).
export function takeScopeSnapshot(inspection: V2Inspection, takenAt: number): InspectionScopeSnapshot | null {
  const state = assertedPropertyState(inspection.property);
  if (state !== 'NY' || jurisdictionStatusOf('NY') !== 'supported') return null;
  const facts = snapshotPropertyFactsOf(inspection);
  const agreement = agreementScopeFor(inspection.agreement.documentVersion);
  const block = legalReviewBlockOf(inspection);
  return {
    jurisdiction: 'NY',
    authorityVersion: NY_AUTHORITY_VERSION,
    agreementAuthorityVersion: inspection.agreement.documentVersion,
    checklistAuthorityVersion: NY_CHECKLIST_AUTHORITY_VERSION,
    takenAt,
    propertyFacts: facts,
    dispositions: deriveDispositions(facts, agreement),
    contractAdded: [...agreement.contractAdded],
    clientExclusions: [...agreement.clientExclusions],
    contextKind: contextKindOf(inspection),
    legalReviewBlock: block ? { blockerId: block.blockerId, lrIds: [...block.lrIds] } : null
  };
}

// The snapshot an inspection should carry after an agreement write: one at signing, none while the
// document is open. A snapshot is never back-filled onto an inspection that was signed before this
// round — that would forge provenance (plan §6).
export function scopeSnapshotForAgreement(inspection: V2Inspection, now: number): InspectionScopeSnapshot | null {
  if (!agreementSigned(inspection.agreement)) return null;
  return takeScopeSnapshot(inspection, inspection.agreement.signedAt ?? now);
}

// ---------- the packet (AI policy §7) ----------

export interface NyScopePacket {
  jurisdiction: 'NY';
  authorityVersion: string;
  agreementAuthorityVersion: string;
  checklistAuthorityVersion: string;
  contextKind: InspectionContextKind;
  propertyFacts: SnapshotPropertyFacts;
  dispositions: Record<string, ScopeDispositionRecord>;
  contractAdded: readonly string[];
  clientExclusions: readonly string[];
}

export type ScopePacketUnavailable = 'agreementUnsigned' | 'noSnapshot' | 'authorityMismatch' | 'legalReviewBlock';

export type ScopePacketState = { kind: 'ready'; packet: NyScopePacket } | { kind: 'unavailable'; reason: ScopePacketUnavailable };

// Fail-closed (plan §6): no snapshot, an unsigned agreement, another authority version or a
// legal-review block means there is NO New York packet for this inspection — and without a packet
// no AI result may be admitted. The gate and the AI pre-conditions read this one function.
//
// Owner 2026-10-07 (「演示免签」): a Demo / internal test is not a client engagement — its field work
// never waited for a signature, so its AI does not either. Without a snapshot frozen at signing, its
// packet is derived from the property facts as they stand now, under the same New York rules (a
// Demo whose property is not an asserted New York property still has no packet). A real client
// engagement is unchanged: only the snapshot frozen at signing counts.
export function scopePacketFor(inspection: V2Inspection): ScopePacketState {
  if (contextKindOf(inspection) === 'nonClientTest' && inspection.scopeSnapshot === null) {
    const live = takeScopeSnapshot(inspection, inspection.updatedAt);
    if (!live) return { kind: 'unavailable', reason: 'noSnapshot' };
    if (live.legalReviewBlock !== null) return { kind: 'unavailable', reason: 'legalReviewBlock' };
    return { kind: 'ready', packet: scopePacketFromSnapshot(live) };
  }
  if (!agreementSigned(inspection.agreement)) return { kind: 'unavailable', reason: 'agreementUnsigned' };
  const snapshot = inspection.scopeSnapshot;
  if (!snapshot) return { kind: 'unavailable', reason: 'noSnapshot' };
  if (snapshot.jurisdiction !== 'NY' || snapshot.authorityVersion !== NY_AUTHORITY_VERSION) return { kind: 'unavailable', reason: 'authorityMismatch' };
  if (snapshot.legalReviewBlock !== null) return { kind: 'unavailable', reason: 'legalReviewBlock' };
  return { kind: 'ready', packet: scopePacketFromSnapshot(snapshot) };
}

// The packet slice the gate reads (AI policy §7). It is derived from the snapshot and nothing else:
// the gate never sees "latest", only what was frozen at signing (runtime contract §6).
export function scopePacketFromSnapshot(snapshot: InspectionScopeSnapshot): NyScopePacket {
  return {
    jurisdiction: 'NY',
    authorityVersion: snapshot.authorityVersion,
    agreementAuthorityVersion: snapshot.agreementAuthorityVersion,
    checklistAuthorityVersion: snapshot.checklistAuthorityVersion,
    contextKind: snapshot.contextKind,
    propertyFacts: snapshot.propertyFacts,
    dispositions: snapshot.dispositions,
    contractAdded: snapshot.contractAdded,
    clientExclusions: snapshot.clientExclusions
  };
}

// ---------- the gate ----------

export type GateAction = 'ALLOWED' | 'CONVERTED' | 'CONVERTED_TO_LIMITATION' | 'REJECTED_BY_SCOPE' | 'LEGAL_REVIEW_BLOCK';

export type GateConvertedTo = 'otherIssue' | 'needsClarification' | 'limitationPrompt' | 'outOfScopeNote';

export type GateReason =
  | 'inScope'
  | 'inspectorAddedRow'
  | 'inspectorDefinedArea'
  | 'conditionalConfirmed'
  | 'conditionalUnconfirmed'
  | 'outOfScopeRow'
  | 'outOfScopeTask'
  | 'outOfScopeObservation'
  | 'inScopeHazard'
  | 'threatensBuilding'
  | 'visibleEvidenceObservation'
  | 'scopeQuestion'
  | 'evaluationQuestion'
  | 'obstruction'
  | 'unknownSubject'
  | 'noAction'
  | 'legalReviewRequired'
  | 'legalReviewBlock'
  | 'noScopePacket';

// Why a decision is not surfaced even though scope allowed it (AI policy §3 step 5, PP-13).
export type GateSuppression = 'duplicate' | 'lowConfidenceTask' | 'alreadySettled' | 'prohibitedOnly';

// What the inspector may do with the card. The store enforces this list, so no screen can offer an
// action the authority does not allow — a task from an out-of-scope subject above all.
export type GateInspectorAction = 'findingOnItem' | 'otherFinding' | 'addItem' | 'limitation' | 'voluntaryObservation' | 'acknowledge' | 'askAgain' | 'ignore';

// Product-authored explanations the card shows. Codes, not text: the App Language wording lives in
// the two dictionaries (basis wording per NY-REPORT-AUTHORITY.md §4.12).
export type GateNoteCode =
  | 'notRequiredNotIncluded'
  | 'notPartOfNyHomeInspection'
  | 'clientExcluded'
  | 'uncertainSubject'
  | 'presenceQuestion'
  | 'limitationPrompt'
  | 'pestObservationOnly'
  | 'sentenceRemoved';

export interface GateLimitation {
  // The in-scope row the limitation belongs on (null = the inspector picks it).
  targetItemId: string | null;
  targetRow: string | null;
  reasonCode: UnableReason;
  templateId: LimitationTemplateId | null;
  // Always a PORTION: an obstruction is never a whole-component Unable (PP-22).
  scope: 'partial';
}

export interface GateProhibitedHit {
  phraseId: ProhibitedPhraseId;
  field: 'title' | 'headline' | 'observation' | 'recommendation';
  sentence: string;
}

export interface GateDecision {
  resultId: string;
  subjectId: ScopeSubjectId | null;
  legalStatus: LegalStatus | null;
  disposition: ScopeDisposition | null;
  action: GateAction;
  convertedTo: GateConvertedTo | null;
  reason: GateReason;
  ruleIds: string[];
  authorityVersion: string;
  noteCodes: GateNoteCode[];
  prohibitedHits: GateProhibitedHit[];
  suppressed: GateSuppression | null;
  limitation: GateLimitation | null;
  descriptorPrompt: 'waterSupplySource' | 'wasteDisposalSource' | null;
  allowedActions: GateInspectorAction[];
  // The checklist row the result came from, when the gate took it off the row (an out-of-scope
  // observation-only row) — kept for context, never as a task.
  relatedItemId: string | null;
  // The content as it should be stored: converted type, prohibited sentences removed.
  content: AiObservationContent;
}

// What a stored suggestion carries from its decision (persisted; absent on every record written
// before this round, which reads back as `null` = ungated).
export interface SuggestionScope {
  authorityVersion: string;
  subjectId: ScopeSubjectId | null;
  legalStatus: LegalStatus | null;
  disposition: ScopeDisposition | null;
  action: GateAction;
  convertedTo: GateConvertedTo | null;
  reason: GateReason;
  ruleIds: string[];
  noteCodes: GateNoteCode[];
  prohibitedHits: GateProhibitedHit[];
  limitation: GateLimitation | null;
  descriptorPrompt: 'waterSupplySource' | 'wasteDisposalSource' | null;
  allowedActions: GateInspectorAction[];
  relatedItemId: string | null;
}

// ---------- runtime guards (the schema reads records back through these) ----------

export const GATE_ACTION_ORDER: readonly GateAction[] = Object.freeze(['ALLOWED', 'CONVERTED', 'CONVERTED_TO_LIMITATION', 'REJECTED_BY_SCOPE', 'LEGAL_REVIEW_BLOCK']);
export const GATE_CONVERTED_TO_ORDER: readonly GateConvertedTo[] = Object.freeze(['otherIssue', 'needsClarification', 'limitationPrompt', 'outOfScopeNote']);
export const GATE_REASON_ORDER: readonly GateReason[] = Object.freeze([
  'inScope',
  'inspectorAddedRow',
  'inspectorDefinedArea',
  'conditionalConfirmed',
  'conditionalUnconfirmed',
  'outOfScopeRow',
  'outOfScopeTask',
  'outOfScopeObservation',
  'inScopeHazard',
  'threatensBuilding',
  'visibleEvidenceObservation',
  'scopeQuestion',
  'evaluationQuestion',
  'obstruction',
  'unknownSubject',
  'noAction',
  'legalReviewRequired',
  'legalReviewBlock',
  'noScopePacket'
]);
export const GATE_SUPPRESSION_ORDER: readonly GateSuppression[] = Object.freeze(['duplicate', 'lowConfidenceTask', 'alreadySettled', 'prohibitedOnly']);
export const GATE_INSPECTOR_ACTION_ORDER: readonly GateInspectorAction[] = Object.freeze(['findingOnItem', 'otherFinding', 'addItem', 'limitation', 'voluntaryObservation', 'acknowledge', 'askAgain', 'ignore']);
export const GATE_NOTE_CODE_ORDER: readonly GateNoteCode[] = Object.freeze([
  'notRequiredNotIncluded',
  'notPartOfNyHomeInspection',
  'clientExcluded',
  'uncertainSubject',
  'presenceQuestion',
  'limitationPrompt',
  'pestObservationOnly',
  'sentenceRemoved'
]);

export function isGateAction(value: unknown): value is GateAction {
  return typeof value === 'string' && (GATE_ACTION_ORDER as readonly string[]).includes(value);
}
export function isGateConvertedTo(value: unknown): value is GateConvertedTo {
  return typeof value === 'string' && (GATE_CONVERTED_TO_ORDER as readonly string[]).includes(value);
}
export function isGateReason(value: unknown): value is GateReason {
  return typeof value === 'string' && (GATE_REASON_ORDER as readonly string[]).includes(value);
}
export function isGateSuppression(value: unknown): value is GateSuppression {
  return typeof value === 'string' && (GATE_SUPPRESSION_ORDER as readonly string[]).includes(value);
}
export function isGateInspectorAction(value: unknown): value is GateInspectorAction {
  return typeof value === 'string' && (GATE_INSPECTOR_ACTION_ORDER as readonly string[]).includes(value);
}
export function isGateNoteCode(value: unknown): value is GateNoteCode {
  return typeof value === 'string' && (GATE_NOTE_CODE_ORDER as readonly string[]).includes(value);
}
export function isInspectionContextKind(value: unknown): value is InspectionContextKind {
  return value === 'realClient' || value === 'nonClientTest';
}

export function scopeOfDecision(decision: GateDecision): SuggestionScope {
  return {
    authorityVersion: decision.authorityVersion,
    subjectId: decision.subjectId,
    legalStatus: decision.legalStatus,
    disposition: decision.disposition,
    action: decision.action,
    convertedTo: decision.convertedTo,
    reason: decision.reason,
    ruleIds: [...decision.ruleIds],
    noteCodes: [...decision.noteCodes],
    prohibitedHits: decision.prohibitedHits.map((h) => ({ ...h })),
    limitation: decision.limitation ? { ...decision.limitation } : null,
    descriptorPrompt: decision.descriptorPrompt,
    allowedActions: [...decision.allowedActions],
    relatedItemId: decision.relatedItemId
  };
}

// How a card is rendered. The inspector never sees an Axis name (plan §8.5); these three are
// presentation kinds, not scope vocabulary.
export type SuggestionPresentation = 'standard' | 'scopeReminder' | 'limitationPrompt';

export function presentationOfScope(scope: SuggestionScope | null): SuggestionPresentation {
  if (!scope) return 'standard';
  if (scope.action === 'REJECTED_BY_SCOPE') return 'scopeReminder';
  if (scope.action === 'CONVERTED_TO_LIMITATION') return 'limitationPrompt';
  return 'standard';
}

export function gateSurfaces(decision: GateDecision): boolean {
  return decision.suppressed === null && decision.action !== 'LEGAL_REVIEW_BLOCK';
}

export interface GateInput {
  id: string;
  content: AiObservationContent;
}

export interface GateContext {
  packet: NyScopePacket;
  areaKind: AreaKind;
  // The rows of the area the results came from.
  areaItems: readonly V2ChecklistItem[];
  // Every row of the inspection — limitation targets may live in another area (the roof row for a
  // solar array seen from the exterior).
  inspectionItems: readonly V2ChecklistItem[];
  // Enabled area kinds, for confirming a conditional trigger from the inspector's own layout.
  areaKinds: readonly AreaKind[];
  // The inspection's CURRENT property facts: a trigger the inspector confirmed after signing counts
  // (AI policy §3 step 3 "confirmed by property facts or the inspector"), while the frozen
  // dispositions themselves never move.
  propertyFacts: SnapshotPropertyFacts;
}

// ---------- keyword resolution (AI policy §3 step 1) ----------

interface CompiledKeyword {
  weight: number;
  pattern: RegExp | null;
  zh: string | null;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const COMPILED: ReadonlyMap<ScopeSubjectId, readonly CompiledKeyword[]> = (() => {
  const map = new Map<ScopeSubjectId, CompiledKeyword[]>();
  for (const s of NY_SCOPE_SUBJECTS) {
    const list: CompiledKeyword[] = [];
    for (const kw of s.keywords.en) {
      list.push({ weight: kw.length, pattern: new RegExp(`\\b${escapeRegExp(kw).replace(/\s+/g, '\\s+')}\\b`, 'i'), zh: null });
    }
    // Chinese carries more meaning per character, so a Chinese keyword weighs double: "太阳能" (3)
    // must beat "屋面" (2) the way "solar panels" beats "roof".
    for (const kw of s.keywords.zh) list.push({ weight: kw.length * 2, pattern: null, zh: kw });
    map.set(s.id, list);
  }
  return map;
})();

// The longest keyword of this subject that appears in the text, or 0. Longest match wins, so the
// specific subject ("solar panels", "window air conditioner") beats the general one ("panel", "air
// conditioning"); ties are broken by catalogue order, which makes resolution deterministic.
function keywordScore(text: string, subject: ScopeSubject): number {
  let best = 0;
  for (const kw of COMPILED.get(subject.id) ?? []) {
    if (kw.pattern ? kw.pattern.test(text) : kw.zh !== null && text.includes(kw.zh)) best = Math.max(best, kw.weight);
  }
  return best === 0 ? 0 : best + subject.keywordBonus;
}

function resolutionText(content: AiObservationContent): string {
  return [content.title, content.headline, content.observation, content.question ?? ''].filter((s) => s.length > 0).join('\n');
}

function matchesAny(text: string, patterns: readonly RegExp[]): boolean {
  return patterns.some((p) => p.test(text));
}

function resolveByKeyword(text: string, options: { includeObstructionOnly: boolean }): ScopeSubject | null {
  let best: ScopeSubject | null = null;
  let bestScore = 0;
  for (const s of NY_SCOPE_SUBJECTS) {
    if (s.obstructionOnly && !options.includeObstructionOnly) continue;
    const score = keywordScore(text, s);
    if (score > bestScore) {
      best = s;
      bestScore = score;
    }
  }
  return best;
}

// The subject a result's own words name, leaving the given subjects out — the same keywords and weights as
// step 1. 2026-10-28: the professional for a result the gate located only by a location subject (the basement,
// the attic …) comes from the thing the text names (src/domain/inspectionRules.ts tradeForResult); the gate's
// own decision is not changed by this.
export function keywordSubjectOf(content: AiObservationContent, exclude: readonly ScopeSubjectId[]): ScopeSubjectId | null {
  const text = resolutionText(content);
  let best: ScopeSubject | null = null;
  let bestScore = 0;
  for (const s of NY_SCOPE_SUBJECTS) {
    if (s.obstructionOnly || exclude.includes(s.id)) continue;
    const score = keywordScore(text, s);
    if (score > bestScore) {
      best = s;
      bestScore = score;
    }
  }
  return best ? best.id : null;
}

// An obstruction is only an obstruction when the text also says something was not observed
// (NY-INSPECTION-LIMITATIONS.md §4). "Stored items against the water heater" is a water-heater
// observation; "stored items concealed the foundation wall" is a limitation.
function resolveObstruction(text: string): ScopeSubject | null {
  if (!matchesAny(text, CONCEALMENT_PATTERNS)) return null;
  let best: ScopeSubject | null = null;
  let bestScore = 0;
  for (const s of NY_SCOPE_SUBJECTS) {
    if (s.obstruction === null) continue;
    const score = keywordScore(text, s);
    if (score > bestScore) {
      best = s;
      bestScore = score;
    }
  }
  return best;
}

// ---------- trigger confirmation ----------

function rowIsPresent(items: readonly V2ChecklistItem[], rows: readonly string[]): boolean {
  return items.some((i) => i.templateId !== null && rows.includes(i.templateId) && !(i.status === 'notApplicable' || (i.status === 'unableToInspect' && i.reasonCode === 'notInstalled')));
}

// Whether the inspector (never the AI) has confirmed that a conditional subject exists here.
function triggerConfirmed(s: ScopeSubject, ctx: GateContext): boolean {
  for (const trigger of s.triggers) {
    if (trigger.kind === 'propertyFact') {
      const value = propertyFactValue(ctx.propertyFacts, trigger.fact);
      if (value !== null && trigger.oneOf.includes(value)) return true;
    } else if (trigger.kind === 'areaKind') {
      if (ctx.areaKinds.includes(trigger.areaKind)) return true;
    } else if (rowIsPresent(ctx.inspectionItems, s.rows)) return true;
  }
  return false;
}

// ---------- step 4: the prohibited-conclusion filter ----------

const REPORT_BOUND_FIELDS: ReadonlyArray<'title' | 'headline' | 'observation' | 'recommendation'> = Object.freeze(['title', 'headline', 'observation', 'recommendation']);

function filterProhibited(content: AiObservationContent): { content: AiObservationContent; hits: GateProhibitedHit[] } {
  const next: AiObservationContent = { ...content };
  const hits: GateProhibitedHit[] = [];
  for (const field of REPORT_BOUND_FIELDS) {
    const value = next[field];
    if (typeof value !== 'string' || value.trim().length === 0) continue;
    const stripped = stripProhibitedSentences(value, 'ai');
    if (stripped.hits.length === 0) continue;
    next[field] = stripped.text;
    for (const hit of stripped.hits) hits.push({ phraseId: hit.phraseId, field, sentence: hit.sentence });
  }
  // A title that was nothing but a prohibited conclusion falls back to the headline, so the card
  // still names its subject instead of showing an empty line.
  if (next.title.trim().length === 0 && next.headline.trim().length > 0) next.title = next.headline;
  return { content: next, hits };
}

function contentIsEmpty(content: AiObservationContent): boolean {
  return REPORT_BOUND_FIELDS.every((field) => (content[field] ?? '').trim().length === 0);
}

// ---------- the action table (AI policy §3 step 3) ----------

type ScopeClass = 'inScope' | 'conditionalUnconfirmed' | 'excluded' | 'unknown';

function classOf(disposition: ScopeDisposition | null, confirmed: boolean): ScopeClass {
  switch (disposition) {
    case 'MANDATORY_IN_SCOPE':
    case 'CONTRACT_INCLUDED':
      return 'inScope';
    case 'CONDITIONAL_SCOPE':
      return confirmed ? 'inScope' : 'conditionalUnconfirmed';
    case 'OUT_OF_SCOPE_PRODUCT_POLICY':
    case 'STATUTORY_EXCLUSION':
    case 'CLIENT_EXCLUDED_IF_LAWFULLY_PERMITTED':
    case 'NOT_INSTALLED_NA':
      return 'excluded';
    case 'EXPLICIT_PROHIBITION':
      return 'excluded';
    case 'LEGAL_REVIEW_REQUIRED':
    case 'LEGAL_REVIEW_BLOCK':
      return 'unknown';
    default:
      return 'unknown';
  }
}

function basisNote(disposition: ScopeDisposition | null): GateNoteCode {
  if (disposition === 'STATUTORY_EXCLUSION') return 'notPartOfNyHomeInspection';
  if (disposition === 'CLIENT_EXCLUDED_IF_LAWFULLY_PERMITTED') return 'clientExcluded';
  return 'notRequiredNotIncluded';
}

interface Draft {
  subject: ScopeSubject | null;
  disposition: ScopeDisposition | null;
  action: GateAction;
  convertedTo: GateConvertedTo | null;
  reason: GateReason;
  noteCodes: GateNoteCode[];
  extraRuleIds: string[];
  limitation: GateLimitation | null;
  allowedActions: GateInspectorAction[];
  relatedItemId: string | null;
  type: AiObservationKind;
  itemId: string | null;
  responsibleTrade: AiObservationContent['responsibleTrade'];
}

function limitationTargetFor(s: ScopeSubject, matchedItem: V2ChecklistItem | null, ctx: GateContext): GateLimitation {
  const obstruction = s.obstruction!;
  if (matchedItem && canCarryLimitedPortions(matchedItem.status)) {
    return { targetItemId: matchedItem.id, targetRow: matchedItem.templateId, reasonCode: obstruction.reasonCode, templateId: obstruction.templateId, scope: 'partial' };
  }
  const rows: string[] = obstruction.targetRow !== null ? [obstruction.targetRow] : obstruction.limitationOf.flatMap((id) => [...scopeSubject(id).rows]);
  for (const row of rows) {
    const item = ctx.inspectionItems.find((i) => i.templateId === row && canCarryLimitedPortions(i.status));
    if (item) return { targetItemId: item.id, targetRow: row, reasonCode: obstruction.reasonCode, templateId: obstruction.templateId, scope: 'partial' };
  }
  return { targetItemId: null, targetRow: obstruction.targetRow, reasonCode: obstruction.reasonCode, templateId: obstruction.templateId, scope: 'partial' };
}

function decideOne(input: GateInput, ctx: GateContext): GateDecision {
  const original = input.content;
  const text = resolutionText(original);
  const matchedItem = original.type === 'existingItem' && original.itemId ? (ctx.areaItems.find((i) => i.id === original.itemId) ?? null) : null;
  const draft: Draft = {
    subject: null,
    disposition: null,
    action: 'ALLOWED',
    convertedTo: null,
    reason: 'inScope',
    noteCodes: [],
    extraRuleIds: [],
    limitation: null,
    allowedActions: [],
    relatedItemId: null,
    type: original.type,
    itemId: original.itemId,
    responsibleTrade: original.responsibleTrade
  };

  // Step 1 — subject resolution. A row the inspector added themselves is their own work and is
  // never gated (AI policy §3: "Inspector-initiated findings, results and notes are never gated").
  const obstruction = resolveObstruction(text);
  const rowSubjectId = matchedItem ? subjectOfTemplateRow(matchedItem.templateId) : null;
  const rowSubject = rowSubjectId ? scopeSubject(rowSubjectId) : null;
  const keywordSubject = resolveByKeyword(text, { includeObstructionOnly: false });
  const subject = rowSubject ?? (obstruction ?? keywordSubject);
  draft.subject = subject;

  // Step 2 — disposition lookup in the frozen snapshot, with the conditional trigger evaluated
  // against what the INSPECTOR has confirmed since.
  const record = subject ? ctx.packet.dispositions[subject.id] : undefined;
  draft.disposition = record ? record.disposition : null;
  const confirmed = subject !== null && subject.legalStatus === 'CONDITIONAL_MANDATORY' && triggerConfirmed(subject, ctx);
  const isLimitation = obstruction !== null && (rowSubject === null || scopeClassAllowsLimitation(draft.disposition));

  if (matchedItem && matchedItem.templateId === null) {
    // An inspector-added row: allowed as the inspector's own work.
    applyAllowed(draft, 'inspectorAddedRow');
    draft.allowedActions = ['findingOnItem', 'askAgain', 'ignore'];
    return finish(input, original, draft, ctx);
  }
  if (subject && subject.id === 'inspector-defined-area') {
    applyAllowed(draft, 'inspectorDefinedArea');
    draft.allowedActions = matchedItem ? ['findingOnItem', 'askAgain', 'ignore'] : ['otherFinding', 'askAgain', 'ignore'];
    return finish(input, original, draft, ctx);
  }

  // Step 3 — the action table.
  if (isLimitation && obstruction !== null) {
    draft.subject = obstruction;
    draft.disposition = ctx.packet.dispositions[obstruction.id]?.disposition ?? null;
    draft.action = 'CONVERTED_TO_LIMITATION';
    draft.convertedTo = 'limitationPrompt';
    draft.reason = 'obstruction';
    draft.noteCodes = ['limitationPrompt'];
    draft.extraRuleIds = ['PP-22', 'NY-SOP-04'];
    draft.limitation = limitationTargetFor(obstruction, matchedItem, ctx);
    draft.relatedItemId = matchedItem ? matchedItem.id : null;
    draft.allowedActions = ['limitation', 'ignore'];
    return finish(input, original, draft, ctx);
  }

  const scopeClass: ScopeClass = classOf(draft.disposition, confirmed);
  switch (original.type) {
    case 'noAction':
      applyAllowed(draft, 'noAction');
      draft.allowedActions = ['acknowledge', 'ignore'];
      break;
    case 'existingItem':
      decideExistingItem(draft, scopeClass, subject, matchedItem, confirmed);
      break;
    case 'suggestedItem':
      decideSuggestedItem(draft, scopeClass, subject);
      break;
    case 'otherIssue':
      decideOtherIssue(draft, scopeClass, subject, text);
      break;
    case 'needsClarification':
      decideNeedsClarification(draft, scopeClass, subject, text);
      break;
  }
  return finish(input, original, draft, ctx);
}

// An obstruction only converts a row that is itself in scope; an obstruction "on" an out-of-scope
// row is out of scope, not a limitation.
function scopeClassAllowsLimitation(disposition: ScopeDisposition | null): boolean {
  return disposition === 'MANDATORY_IN_SCOPE' || disposition === 'CONTRACT_INCLUDED' || disposition === 'CONDITIONAL_SCOPE';
}

function applyAllowed(draft: Draft, reason: GateReason): void {
  draft.action = 'ALLOWED';
  draft.convertedTo = null;
  draft.reason = reason;
}

function decideExistingItem(draft: Draft, scopeClass: ScopeClass, subject: ScopeSubject | null, matchedItem: V2ChecklistItem | null, confirmed: boolean): void {
  if (scopeClass === 'inScope') {
    applyAllowed(draft, confirmed ? 'conditionalConfirmed' : 'inScope');
    draft.allowedActions = matchedItem ? ['findingOnItem', 'askAgain', 'ignore'] : ['otherFinding', 'askAgain', 'ignore'];
    return;
  }
  if (scopeClass === 'conditionalUnconfirmed') {
    draft.action = 'CONVERTED';
    draft.convertedTo = 'needsClarification';
    draft.type = 'needsClarification';
    draft.reason = 'conditionalUnconfirmed';
    draft.noteCodes = ['presenceQuestion'];
    draft.allowedActions = ['addItem', 'acknowledge', 'ignore'];
    return;
  }
  if (scopeClass === 'excluded') {
    // The row exists only because the product keeps an observation-only row for it. The result may
    // be recorded as an observation; it is never upgraded to an evaluation of the excluded system.
    draft.action = 'CONVERTED';
    draft.convertedTo = 'otherIssue';
    draft.type = 'otherIssue';
    draft.relatedItemId = matchedItem ? matchedItem.id : null;
    draft.itemId = null;
    draft.reason = 'outOfScopeRow';
    draft.noteCodes = [basisNote(draft.disposition)];
    if (subject && subject.id === 'pest-wdi') {
      draft.noteCodes.push('pestObservationOnly');
      if (draft.responsibleTrade === null) draft.responsibleTrade = 'licensedPestProfessional';
    }
    draft.allowedActions = ['otherFinding', 'ignore'];
    return;
  }
  applyAllowed(draft, subject ? 'legalReviewRequired' : 'unknownSubject');
  draft.noteCodes = ['uncertainSubject'];
  draft.allowedActions = matchedItem ? ['findingOnItem', 'askAgain', 'ignore'] : ['otherFinding', 'askAgain', 'ignore'];
}

function decideSuggestedItem(draft: Draft, scopeClass: ScopeClass, subject: ScopeSubject | null): void {
  if (scopeClass === 'inScope') {
    applyAllowed(draft, 'inScope');
    draft.extraRuleIds = ['PP-11'];
    draft.allowedActions = ['addItem', 'otherFinding', 'ignore'];
    return;
  }
  if (scopeClass === 'conditionalUnconfirmed') {
    draft.action = 'CONVERTED';
    draft.convertedTo = 'needsClarification';
    draft.type = 'needsClarification';
    draft.reason = 'conditionalUnconfirmed';
    draft.noteCodes = ['presenceQuestion'];
    draft.extraRuleIds = ['PP-11'];
    draft.allowedActions = ['addItem', 'acknowledge', 'ignore'];
    return;
  }
  if (scopeClass === 'excluded') {
    // OD-13: the scope reminder. Never a task, never a defect, never in the report draft.
    draft.action = 'REJECTED_BY_SCOPE';
    draft.convertedTo = 'outOfScopeNote';
    draft.reason = 'outOfScopeTask';
    draft.noteCodes = [basisNote(draft.disposition)];
    draft.extraRuleIds = ['PP-11', 'PP-18', 'OD-13'];
    draft.allowedActions = ['voluntaryObservation', 'acknowledge'];
    return;
  }
  // Unknown subject: one question, never a task (AI policy §3 step 1 conservative default).
  draft.action = 'CONVERTED';
  draft.convertedTo = 'needsClarification';
  draft.type = 'needsClarification';
  draft.reason = subject ? 'legalReviewRequired' : 'unknownSubject';
  draft.noteCodes = ['uncertainSubject'];
  draft.extraRuleIds = ['PP-19'];
  draft.allowedActions = ['acknowledge', 'ignore'];
}

function decideOtherIssue(draft: Draft, scopeClass: ScopeClass, subject: ScopeSubject | null, text: string): void {
  if (scopeClass === 'inScope') {
    applyAllowed(draft, 'inScope');
    draft.allowedActions = ['otherFinding', 'addItem', 'askAgain', 'ignore'];
    return;
  }
  if (scopeClass === 'conditionalUnconfirmed') {
    applyAllowed(draft, 'conditionalUnconfirmed');
    draft.allowedActions = ['otherFinding', 'askAgain', 'ignore'];
    return;
  }
  if (scopeClass === 'excluded') {
    const hazard = matchesAny(text, HAZARD_PATTERNS);
    const threat = matchesAny(text, BUILDING_THREAT_PATTERNS);
    const observationAllowed = subject !== null && subject.otherIssuePolicy === 'observationAllowed';
    if (hazard || threat || observationAllowed) {
      applyAllowed(draft, hazard ? 'inScopeHazard' : threat ? 'threatensBuilding' : 'visibleEvidenceObservation');
      draft.noteCodes = [basisNote(draft.disposition)];
      if (subject && subject.id === 'pest-wdi') {
        draft.noteCodes.push('pestObservationOnly');
        if (draft.responsibleTrade === null) draft.responsibleTrade = 'licensedPestProfessional';
      }
      // Never a task on a subject this inspection does not cover.
      draft.allowedActions = ['otherFinding', 'askAgain', 'ignore'];
      return;
    }
    draft.action = 'REJECTED_BY_SCOPE';
    draft.convertedTo = 'outOfScopeNote';
    draft.reason = 'outOfScopeObservation';
    draft.noteCodes = [basisNote(draft.disposition)];
    draft.extraRuleIds = ['PP-18', 'OD-13'];
    draft.allowedActions = ['voluntaryObservation', 'acknowledge'];
    return;
  }
  applyAllowed(draft, subject ? 'legalReviewRequired' : 'unknownSubject');
  draft.noteCodes = ['uncertainSubject'];
  draft.allowedActions = ['otherFinding', 'askAgain', 'ignore'];
}

function decideNeedsClarification(draft: Draft, scopeClass: ScopeClass, subject: ScopeSubject | null, text: string): void {
  if (scopeClass === 'inScope' || scopeClass === 'conditionalUnconfirmed') {
    applyAllowed(draft, scopeClass === 'inScope' ? 'inScope' : 'conditionalUnconfirmed');
    draft.allowedActions = scopeClass === 'inScope' ? ['addItem', 'otherFinding', 'askAgain', 'ignore'] : ['addItem', 'acknowledge', 'ignore'];
    if (scopeClass === 'conditionalUnconfirmed') draft.noteCodes = ['presenceQuestion'];
    return;
  }
  if (scopeClass === 'excluded') {
    if (matchesAny(text, PRESENCE_QUESTION_PATTERNS)) {
      applyAllowed(draft, 'scopeQuestion');
      draft.noteCodes = [basisNote(draft.disposition)];
      draft.allowedActions = ['acknowledge', 'ignore'];
      return;
    }
    draft.action = 'REJECTED_BY_SCOPE';
    draft.convertedTo = 'outOfScopeNote';
    draft.reason = 'evaluationQuestion';
    draft.noteCodes = [basisNote(draft.disposition)];
    draft.extraRuleIds = ['PP-18', 'OD-13'];
    draft.allowedActions = ['voluntaryObservation', 'acknowledge'];
    return;
  }
  applyAllowed(draft, subject ? 'legalReviewRequired' : 'unknownSubject');
  draft.noteCodes = ['uncertainSubject'];
  draft.allowedActions = ['acknowledge', 'ignore'];
}

function finish(input: GateInput, original: AiObservationContent, draft: Draft, ctx: GateContext): GateDecision {
  const converted: AiObservationContent = { ...original, type: draft.type, itemId: draft.type === 'existingItem' ? draft.itemId : null, responsibleTrade: draft.responsibleTrade };
  // Step 4 — the prohibited-conclusion filter, on the report-bound fields only.
  const filtered = filterProhibited(converted);
  const noteCodes = [...draft.noteCodes];
  if (filtered.hits.length > 0) noteCodes.push('sentenceRemoved');
  const subject = draft.subject;
  // Auditability (invariant iv): the subject's rules, whatever the action added, and the rule behind
  // every phrase the guard removed.
  const ruleIds = [...new Set([...(subject ? subject.ruleIds : []), ...draft.extraRuleIds, ...filtered.hits.flatMap((hit) => prohibitedPhrase(hit.phraseId).ruleIds)])];
  return {
    resultId: input.id,
    subjectId: subject ? subject.id : null,
    legalStatus: subject ? subject.legalStatus : null,
    disposition: draft.disposition,
    action: draft.action,
    convertedTo: draft.convertedTo,
    reason: draft.reason,
    ruleIds,
    authorityVersion: ctx.packet.authorityVersion,
    noteCodes,
    prohibitedHits: filtered.hits,
    suppressed: contentIsEmpty(filtered.content) && draft.action !== 'LEGAL_REVIEW_BLOCK' && filtered.hits.length > 0 ? 'prohibitedOnly' : null,
    limitation: draft.limitation,
    descriptorPrompt: subject ? subject.descriptorPrompt : null,
    allowedActions: draft.allowedActions.length > 0 ? draft.allowedActions : ['ignore'],
    // The row the result was about, even where the converted content no longer carries it — the
    // audit trail keeps the association, and the workload pass needs it to see that the inspector
    // already settled that row.
    relatedItemId: draft.relatedItemId ?? (converted.itemId === null ? original.itemId : null),
    content: filtered.content
  };
}

// ---------- step 5: workload safety (AI policy §3 step 5; PP-13) ----------

function applyWorkloadSafety(decisions: GateDecision[], ctx: GateContext): void {
  const seen = new Set<string>();
  for (const decision of decisions) {
    if (!gateSurfaces(decision)) continue;
    // Same subject + same type + same photos = one result (AI policy §3 step 5). The row is
    // deliberately NOT part of the key: a `safe-*` mirror row and its source row resolve to the SAME
    // subject (F-09), so the whole-house re-check does not duplicate the area's own card.
    const key = [decision.subjectId ?? 'unknown', decision.content.type, [...decision.content.photoIds].sort().join(',')].join('|');
    if (seen.has(key)) {
      decision.suppressed = 'duplicate';
      continue;
    }
    seen.add(key);
    if (decision.content.type === 'suggestedItem' && decision.action === 'ALLOWED' && decision.content.confidence === 'low') {
      decision.suppressed = 'lowConfidenceTask';
      continue;
    }
    // A "please confirm" about a row the inspector has already settled is noise.
    const itemId = decision.content.itemId ?? decision.relatedItemId;
    if (decision.content.type === 'needsClarification' && itemId !== null) {
      const item = ctx.areaItems.find((i) => i.id === itemId);
      if (item && item.status !== 'notChecked') decision.suppressed = 'alreadySettled';
    }
  }
}

// THE GATE. Same input, same output — no model call, no clock, no randomness (invariant v).
export function gateAiResults(inputs: readonly GateInput[], ctx: GateContext): GateDecision[] {
  const decisions = inputs.map((input) => decideOne(input, ctx));
  applyWorkloadSafety(decisions, ctx);
  return decisions;
}

// The fail-closed decision list for an inspection that has no New York packet: nothing is admitted,
// every result is kept in the run log with the reason (plan §6; AI policy §3 pre-conditions).
export function gateWithoutPacket(inputs: readonly GateInput[], reason: ScopePacketUnavailable): GateDecision[] {
  return inputs.map((input) => ({
    resultId: input.id,
    subjectId: null,
    legalStatus: null,
    disposition: null,
    action: 'LEGAL_REVIEW_BLOCK' as GateAction,
    convertedTo: null,
    reason: reason === 'legalReviewBlock' ? ('legalReviewBlock' as GateReason) : ('noScopePacket' as GateReason),
    ruleIds: reason === 'legalReviewBlock' ? ['PP-20'] : ['PP-15', 'PP-19'],
    authorityVersion: NY_AUTHORITY_VERSION,
    noteCodes: [],
    prohibitedHits: [],
    suppressed: null,
    limitation: null,
    descriptorPrompt: null,
    allowedActions: ['ignore'] as GateInspectorAction[],
    relatedItemId: null,
    content: input.content
  }));
}

export type { ProhibitedHit };
